/**
 * optionsGlossary — plain-English explanations for the options jargon used across
 * the app, keyed by a short slug. Rendered on demand by the <Term> component
 * (hover / tap to explain) so a user new to options is never blocked by a term.
 *
 * Keep each `plain` to 1–2 sentences, in everyday language, no further jargon.
 * `title` is the friendly display name; `oneLiner` is an ultra-short gloss for chips.
 */
export interface GlossaryEntry {
  title: string;
  oneLiner: string;
  plain: string;
}

export const OPTIONS_GLOSSARY: Record<string, GlossaryEntry> = {
  option: {
    title: 'Option',
    oneLiner: 'A contract to buy/sell at a set price',
    plain: 'A contract that gives the right (not obligation) to buy or sell the underlying at a fixed price before a deadline. You pay a small "premium" for that right.',
  },
  call: {
    title: 'Call (CE)',
    oneLiner: 'Bet the price goes up',
    plain: 'A call option gains value when the underlying price rises. Buyers profit from moves up; sellers collect premium and profit if it does NOT rise much.',
  },
  put: {
    title: 'Put (PE)',
    oneLiner: 'Bet the price goes down',
    plain: 'A put option gains value when the underlying price falls. Buyers profit from moves down; sellers collect premium and profit if it does NOT fall much.',
  },
  premium: {
    title: 'Premium',
    oneLiner: 'The price of the option',
    plain: 'What you pay to buy an option (or receive when you sell one). Like an insurance price — it reflects how likely and how big a move the market expects.',
  },
  strike: {
    title: 'Strike',
    oneLiner: 'The option’s reference price',
    plain: 'The fixed price the option is measured against. E.g. a 95,000 call pays off based on how far above 95,000 the price finishes.',
  },
  expiry: {
    title: 'Expiry',
    oneLiner: 'The option’s deadline',
    plain: 'The date/time the option settles. After expiry it is worth only its final value and stops trading. Nearer expiries decay in value faster.',
  },
  basket: {
    title: 'Basket',
    oneLiner: 'A bundle of option legs traded together',
    plain: 'A pre-packaged combination of option "legs" (buys and sells) that together express one view — e.g. an iron condor. The app places and manages all legs as one position.',
  },
  leg: {
    title: 'Leg',
    oneLiner: 'One option inside a basket',
    plain: 'A single buy or sell of one option. A basket is made of several legs that work together.',
  },
  credit: {
    title: 'Credit strategy',
    oneLiner: 'You get paid up front',
    plain: 'A basket where you receive premium up front (you sell more than you buy). You keep it if the view works out. Usually higher win-rate but capped profit.',
  },
  debit: {
    title: 'Debit strategy',
    oneLiner: 'You pay up front',
    plain: 'A basket where you pay premium up front (you buy more than you sell). Needs a real move to pay off, but risk is limited to what you paid.',
  },
  undefinedRisk: {
    title: 'Undefined risk',
    oneLiner: 'Loss is not capped — be careful',
    plain: 'A structure whose worst-case loss is very large or unlimited (e.g. a naked sell). Only for advanced users, with a stop-loss and small size.',
  },
  maxProfit: {
    title: 'Max profit',
    oneLiner: 'Best case you can make',
    plain: 'The most this basket can earn, at expiry, if everything goes your way.',
  },
  maxLoss: {
    title: 'Max loss',
    oneLiner: 'Worst case you can lose',
    plain: 'The most this basket can lose at expiry. For defined-risk baskets this is fixed and known before you place.',
  },
  breakEven: {
    title: 'Break-even',
    oneLiner: 'Where you neither win nor lose',
    plain: 'The underlying price(s) at which the basket finishes at exactly zero profit. Beyond these you start making money (or losing it).',
  },
  fitScore: {
    title: 'Fit score',
    oneLiner: 'How well this suits your view (0–100)',
    plain: 'A single 0–100 ranking of how well a basket matches YOUR outlook — blending whether you make money when you’re right, win chance, expected return, reward vs risk, and liquidity. Higher = better all-round fit.',
  },
  thesis: {
    title: 'If your view is proven',
    oneLiner: 'Your P&L if you’re just right',
    plain: 'How much you make if the price merely holds your level at expiry — no extra move needed. Baskets are ranked to pay you when your view is proven, not only on big moves.',
  },
  winProbability: {
    title: 'Chance of profit',
    oneLiner: 'How often this finishes in profit',
    plain: 'The chance this basket finishes profitable, worked out against the price distribution the option market itself is implying — the same distribution these premiums were priced from. It replaces an older figure that simply counted how much of a simulated price range was profitable, with every price treated as equally likely; that version rated a long strangle at 96% because most of a wide range sits outside its break-evens. A guide, not a guarantee — and it is shown as a dash rather than estimated when the chain has no usable implied volatility.',
  },
  expectedPnl: {
    title: 'Expected P&L',
    oneLiner: 'Average outcome across scenarios',
    plain: 'The probability-weighted average profit/loss across many possible price paths. Positive means the odds tilt in your favour on average.',
  },
  cvar: {
    title: 'Worst-5% outcome',
    oneLiner: 'A typical bad day',
    plain: 'The average loss in the worst 5% of scenarios — a realistic "how bad could it get" figure, worse than the everyday case but not the absolute maximum.',
  },
  rewardRisk: {
    title: 'Reward : Risk',
    oneLiner: 'Profit vs loss ratio',
    plain: 'Max profit divided by max loss. 2:1 means you can make twice what you risk. Higher is more attractive, but often comes with a lower win chance.',
  },
  margin: {
    title: 'Margin',
    oneLiner: 'Cash the broker blocks',
    plain: 'The amount of capital the exchange holds while the position is open (mainly for sold options). It is returned when you close.',
  },
  greeks: {
    title: 'The Greeks',
    oneLiner: 'How the position reacts',
    plain: 'A set of sensitivities: Delta = direction exposure, Gamma = how fast that changes, Theta = daily time decay, Vega = sensitivity to volatility.',
  },
  delta: {
    title: 'Delta (Δ)',
    oneLiner: 'Direction exposure',
    plain: 'How much the position gains/loses for a 1-unit move up in the underlying. Positive = you benefit from up moves, negative = from down moves.',
  },
  gamma: {
    title: 'Gamma (Γ)',
    oneLiner: 'How fast direction changes',
    plain: 'How quickly your direction exposure (Delta) shifts as price moves. High gamma means the position can swing quickly — good for breakouts, risky for sellers.',
  },
  theta: {
    title: 'Theta (θ)',
    oneLiner: 'Daily time decay',
    plain: 'How much value the position gains/loses each day just from time passing. Positive theta = time works FOR you (typical for credit/income baskets).',
  },
  vega: {
    title: 'Vega (ν)',
    oneLiner: 'Volatility sensitivity',
    plain: 'How much you gain/lose if expected volatility rises. Buyers are usually positive vega (helped by rising volatility); sellers negative.',
  },
  ivRank: {
    title: 'IV Rank',
    oneLiner: 'Is option pricing rich or cheap?',
    plain: 'Where current implied volatility sits vs its own past year (0–100). High = options are expensive (good to sell), low = cheap (good to buy).',
  },
  netToClose: {
    title: 'Net to close',
    oneLiner: 'Cost to exit right now',
    plain: 'The live premium it would take to buy back / sell out of all legs now. Comparing it to your entry credit tells you the current profit or loss.',
  },
  targetPremium: {
    title: 'Take-profit level',
    oneLiner: 'Auto-book profit here',
    plain: 'The net-to-close price at which the app automatically closes the basket to lock in profit (e.g. after capturing 50% of the premium).',
  },
  slPremium: {
    title: 'Stop-loss level',
    oneLiner: 'Auto-cut loss here',
    plain: 'The net-to-close price at which the app automatically closes the basket to cap the loss.',
  },
  straddle: {
    title: 'Short straddle',
    oneLiner: 'Bet on staying calm',
    plain: 'Sell a call and a put at the same strike. You profit if the price stays near that strike; risk grows if it moves far either way.',
  },
  ironCondor: {
    title: 'Iron condor',
    oneLiner: 'Bet on staying in a range',
    plain: 'A defined-risk range trade: profit if the price stays between two levels; losses are capped by protective wings on both sides.',
  },
  paper: {
    title: 'Paper trade',
    oneLiner: 'Practice with no real money',
    plain: 'A simulated position filled at live prices but with NO broker order. It is tracked and auto-managed exactly like a real one, so you can learn risk-free.',
  },
  live: {
    title: 'Live trade',
    oneLiner: 'Real orders, real money',
    plain: 'Places actual orders on the exchange via your broker, subject to the kill-switch and risk gates. Only use after validating with paper trades.',
  },
};

/** Case-insensitive lookup with a couple of common aliases. */
export function glossaryLookup(key: string): GlossaryEntry | null {
  if (!key) return null;
  const k = key.trim();
  if (OPTIONS_GLOSSARY[k]) return OPTIONS_GLOSSARY[k];
  const lower = k.toLowerCase();
  const alias: Record<string, string> = {
    ce: 'call', pe: 'put', 'reward:risk': 'rewardRisk', 'r:r': 'rewardRisk',
    'iv rank': 'ivRank', 'break-even': 'breakEven', breakeven: 'breakEven',
    'max profit': 'maxProfit', 'max loss': 'maxLoss', pnl: 'expectedPnl',
  };
  if (alias[lower]) return OPTIONS_GLOSSARY[alias[lower]];
  const found = Object.keys(OPTIONS_GLOSSARY).find(g => g.toLowerCase() === lower);
  return found ? OPTIONS_GLOSSARY[found] : null;
}
